import { pluginConfig } from '@/globals/pluginConfig';
import { userSettings } from '@/globals/userSettings';

interface StatementTemplateData {
  template: string;
  conformance_options: Record<string, string>;
}

declare global {
  interface Window {
    _cyA11yStatementTemplates?: Record<string, StatementTemplateData>;
  }
}

/**
 * Returns the best matching statement template for the given language code.
 * Falls back to the base language code (e.g. "pt" for "pt-BR"), then to "en".
 */
function getTemplateData(lang: string): StatementTemplateData | null {
  const templates = window._cyA11yStatementTemplates;
  if (!templates) return null;

  // Exact match
  if (templates[lang]) return templates[lang];

  // Base language fallback (e.g. "pt-BR" → "pt")
  const base = lang.split('-')[0];
  if (base !== lang && templates[base]) return templates[base];

  // English fallback
  return templates['en'] ?? null;
}

/**
 * HTML-escapes a string to prevent XSS when inserting user-supplied values.
 */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Formats an ISO date string (YYYY-MM-DD) according to the given locale.
 */
function formatDate(isoDate: string, locale: string): string {
  if (!isoDate) return '';
  try {
    return new Date(isoDate).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

/**
 * Builds the accessibility statement HTML for the current language by substituting
 * all 6 placeholders into the language-specific template.
 *
 * Placeholder resolution rules:
 *  - {{company_name}}, {{website_url}}, {{contact_email}}: escaped DB values.
 *  - {{accessibility_standard}}: escaped DB value (WCAG identifiers are not translated).
 *  - {{conformance_status}}: look up conformance_options[storedValue] from the template;
 *    fall back to the raw stored value if no match (e.g. custom "Other" input).
 *  - {{last_updated}}: generatedDate formatted using the active locale.
 */
export function renderStatement(): string {
  const formData = pluginConfig.modules?.statement?.formData;
  const generatedDate = pluginConfig.modules?.statement?.generatedDate ?? '';

  if (!formData) {
    return '';
  }

  const lang = (userSettings.lang || pluginConfig.language?.default || 'en').toLowerCase();
  const tplData = getTemplateData(lang);

  if (!tplData?.template) {
    return '';
  }

  const conformanceValue = formData.conformanceStatus ?? '';
  const conformanceLabel =
    tplData.conformance_options?.[conformanceValue] ?? conformanceValue;

  const lastUpdated = formatDate(generatedDate, lang);

  return tplData.template
    .replace(/\{\{company_name\}\}/g, escapeHtml(formData.companyName ?? ''))
    .replace(/\{\{website_url\}\}/g, escapeHtml(formData.website ?? ''))
    .replace(/\{\{accessibility_standard\}\}/g, escapeHtml(formData.wcagStandard ?? ''))
    .replace(/\{\{conformance_status\}\}/g, escapeHtml(conformanceLabel))
    .replace(/\{\{contact_email\}\}/g, escapeHtml(formData.businessEmail ?? ''))
    .replace(/\{\{last_updated\}\}/g, escapeHtml(lastUpdated));
}
